/**
 * Entity 4 — Deathmoths.
 *
 * Lore: chimeric moths with two compound eyes and two ocelli, a pair of
 * feathered antennae, six segmented legs ending in clawed tarsi, and three
 * castes. The common ones are hand-sized; the Praetorian is about a metre
 * tall with a 60cm wingspan; the Empress is nearly three metres across. The
 * archives are blunt about the one thing that matters in a game with a torch
 * in it: "Bright lights can attract Deathmoths — if you know Deathmoths are
 * nearby extinguish or dim your lights."
 *
 * This is a Praetorian-scale specimen: a 1.3m wingspan, which is far larger
 * than the common caste and nowhere near the Empress. That size is chosen so
 * it reads as *a moth* rather than as a bird — the wings are broad and blunt,
 * the body is heavy, and it flies badly, which is the difference. Everything
 * about the flight path is noise rather than sine: a moth does not cruise, it
 * fails to fall in a series of directions.
 */

import * as THREE from 'three';
import { damp, lerp, noise1, renderYaw, smoothstep, TAU } from '../core/math';
import type { EntityView } from './api';
import { castShadows, dampAngle, freeAll, Kit, mergeParts, thinBone, type Disposable } from './entkit';

/** Metres from the body centre to a forewing tip. Wingspan is twice this plus the thorax. */
const FORE_SPAN = 0.6;
/** Height the body holds when it is not being thrown around by its own wings. */
const HOVER_Y = 1.15;
/** Wingbeats per second. A real one is far faster; at this rate it reads as beating. */
const BEAT_HZ = 7.5;

/**
 * Forewing outline, root to tip: `[span fraction, leading edge z, trailing edge z]`.
 * Broad, blunt and swept back — a noctuid's wing, not a butterfly's.
 */
const FOREWING: ReadonlyArray<readonly [number, number, number]> = [
  [0.0, -0.09, 0.13],
  [0.16, -0.19, 0.22],
  [0.38, -0.23, 0.25],
  [0.6, -0.23, 0.23],
  [0.78, -0.2, 0.18],
  [0.91, -0.15, 0.09],
  [1.0, -0.07, 0.0],
];

/** Hindwing: shorter, rounder, and mostly hidden under the forewing at rest. */
const HINDWING: ReadonlyArray<readonly [number, number, number]> = [
  [0.0, -0.06, 0.14],
  [0.3, -0.14, 0.24],
  [0.62, -0.15, 0.24],
  [0.85, -0.11, 0.17],
  [1.0, -0.04, 0.05],
];

const WING_BASE = new THREE.Color().setHex(0x6d5c42);
const WING_INNER = new THREE.Color().setHex(0x4a3d2c);
const WING_BAND = new THREE.Color().setHex(0x2c2419);
const WING_FRINGE = new THREE.Color().setHex(0x93856a);
const WING_SPOT = new THREE.Color().setHex(0x9d8e6f);
const _wing = new THREE.Color();

/**
 * Dust-brown with a dark outer band, a pale fringe and one soft discal spot —
 * the markings any moth in a stairwell has. Baked per vertex: a wing is a flat
 * plate and would be a dead grey slab without them.
 */
function wingColor(u: number, v: number): THREE.Color {
  _wing.copy(WING_INNER).lerp(WING_BASE, 1 - Math.abs(v - 0.35));
  const band = smoothstep(0.62, 0.72, u) * (1 - smoothstep(0.84, 0.93, u));
  _wing.lerp(WING_BAND, band * 0.85);
  const spot = 1 - smoothstep(0.0, 0.16, Math.hypot((u - 0.4) * 0.9, (v - 0.42) * 0.6));
  _wing.lerp(WING_SPOT, spot * 0.7);
  const fringe = Math.max(smoothstep(0.93, 1, u), smoothstep(0.9, 1, v));
  return _wing.lerp(WING_FRINGE, fringe * 0.6);
}

/**
 * One wing, as a grid over span and chord. A grid rather than a filled outline
 * because the markings are baked per vertex, and a triangulated silhouette
 * puts its vertices wherever it likes.
 */
function makeWing(profile: ReadonlyArray<readonly [number, number, number]>, span: number, side: number): THREE.BufferGeometry {
  const rows = profile.length;
  const cols = 5;
  const position = new Float32Array(rows * cols * 3);
  const color = new Float32Array(rows * cols * 3);
  const index: number[] = [];

  for (let r = 0; r < rows; r++) {
    const [u, lead, trail] = profile[r]!;
    for (let c = 0; c < cols; c++) {
      const v = c / (cols - 1);
      const i = r * cols + c;
      position[i * 3] = side * u * span;
      // A little camber, so the light does not read it as a flat card.
      position[i * 3 + 1] = Math.sin(v * Math.PI) * 0.022 * (1 - u * 0.6);
      position[i * 3 + 2] = lerp(lead, trail, v);
      const col = wingColor(u, v);
      color[i * 3] = col.r;
      color[i * 3 + 1] = col.g;
      color[i * 3 + 2] = col.b;
    }
  }
  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const a = r * cols + c;
      index.push(a, a + 1, a + cols + 1, a, a + cols + 1, a + cols);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(position, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(color, 3));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

/** A feathered antenna: a shaft with four pairs of barbs, merged into one draw. */
function makeAntenna(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [thinBone(0.17, 0.0055, 0.0018, 3)];
  for (let i = 0; i < 4; i++) {
    for (const side of [-1, 1] as const) {
      const barb = thinBone(0.038 - i * 0.005, 0.0028, 0.0008, 3);
      barb.rotateZ(side * 1.15);
      barb.translate(0, -0.035 - i * 0.032, 0);
      parts.push(barb);
    }
  }
  return mergeParts(parts);
}

/** Six legs, tucked up under the thorax where a flying moth keeps them. */
function makeLegs(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    for (const side of [-1, 1] as const) {
      const femur = thinBone(0.13, 0.011, 0.007, 3);
      femur.rotateZ(side * 1.05);
      const tibia = thinBone(0.12, 0.007, 0.002, 3);
      tibia.rotateZ(side * 0.2);
      tibia.rotateX(-0.9);
      tibia.translate(side * 0.113, -0.064, 0);
      const leg = mergeParts([femur, tibia]);
      leg.translate(side * 0.05, -0.05, -0.06 + i * 0.09);
      parts.push(leg);
    }
  }
  return mergeParts(parts);
}

interface MothKit extends Disposable {
  fore: readonly [THREE.BufferGeometry, THREE.BufferGeometry];
  hind: readonly [THREE.BufferGeometry, THREE.BufferGeometry];
  thorax: THREE.BufferGeometry;
  collar: THREE.BufferGeometry;
  abdomen: THREE.BufferGeometry;
  head: THREE.BufferGeometry;
  eye: THREE.BufferGeometry;
  antenna: THREE.BufferGeometry;
  legs: THREE.BufferGeometry;
  fur: THREE.Material;
  pale: THREE.Material;
  membrane: THREE.Material;
  eyeMat: THREE.Material;
}

/** Rings down the abdomen, so the taper reads as segments rather than a cone. */
function segmentAbdomen(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const bulge = 1 + Math.cos(y * 46) * 0.055;
    pos.setX(i, pos.getX(i) * bulge);
    pos.setZ(i, pos.getZ(i) * bulge);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

function buildMothKit(): MothKit {
  const thorax = new THREE.SphereGeometry(0.13, 9, 6);
  thorax.scale(1.05, 0.95, 1.3);
  // The scale collar over the shoulders, the part of a moth that looks furred.
  const collar = new THREE.SphereGeometry(0.13, 8, 5);
  collar.scale(1.22, 0.62, 0.62);
  collar.translate(0, 0.03, -0.05);

  const abdomen = new THREE.CylinderGeometry(0.105, 0.026, 0.46, 8, 5, false);
  abdomen.translate(0, -0.23, 0);
  segmentAbdomen(abdomen);
  abdomen.rotateX(-1.15);
  abdomen.translate(0, -0.02, 0.16);

  const head = new THREE.SphereGeometry(0.062, 7, 5);
  head.translate(0, -0.01, -0.15);
  const eye = new THREE.SphereGeometry(0.036, 6, 4);

  return {
    fore: [makeWing(FOREWING, FORE_SPAN, -1), makeWing(FOREWING, FORE_SPAN, 1)],
    hind: [makeWing(HINDWING, FORE_SPAN * 0.66, -1), makeWing(HINDWING, FORE_SPAN * 0.66, 1)],
    thorax,
    collar,
    abdomen,
    head,
    eye,
    antenna: makeAntenna(),
    legs: makeLegs(),
    fur: new THREE.MeshLambertMaterial({ color: 0x4b3d2b }),
    pale: new THREE.MeshLambertMaterial({ color: 0x6e5f45 }),
    // Both faces, and no shine: a wing is a dusty membrane whichever side of
    // it you are on.
    membrane: new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide }),
    // Moth eyeshine. Barely lit on its own, but it is the one part of it that
    // answers a torch, which is how you find it in a dark room.
    eyeMat: new THREE.MeshLambertMaterial({ color: 0x201008, emissive: 0x2e0f02 }),
    dispose(): void {
      freeAll([
        ...this.fore,
        ...this.hind,
        this.thorax,
        this.collar,
        this.abdomen,
        this.head,
        this.eye,
        this.antenna,
        this.legs,
        this.fur,
        this.pale,
        this.membrane,
        this.eyeMat,
      ]);
    },
  };
}

const mothKit = new Kit(buildMothKit);

/**
 * Build a Deathmoth. It hovers, it drops, it corrects, and it will not leave
 * a lit room while the light is on.
 */
export function createDeathmothView(): EntityView {
  const kit = mothKit.acquire();
  const object = new THREE.Group();
  const body = new THREE.Group();
  object.add(body);

  body.add(
    new THREE.Mesh(kit.thorax, kit.fur),
    new THREE.Mesh(kit.collar, kit.pale),
    new THREE.Mesh(kit.abdomen, kit.fur),
    new THREE.Mesh(kit.head, kit.fur),
    new THREE.Mesh(kit.legs, kit.fur)
  );

  for (const side of [-1, 1] as const) {
    const eye = new THREE.Mesh(kit.eye, kit.eyeMat);
    eye.position.set(side * 0.05, 0.005, -0.165);
    body.add(eye);

    const antenna = new THREE.Group();
    antenna.position.set(side * 0.035, 0.035, -0.185);
    antenna.rotation.set(2.15, 0, side * 0.62);
    antenna.add(new THREE.Mesh(kit.antenna, kit.pale));
    body.add(antenna);
  }

  const fore: THREE.Group[] = [];
  const hind: THREE.Group[] = [];
  for (let i = 0; i < 2; i++) {
    const f = new THREE.Group();
    f.position.set((i === 0 ? -1 : 1) * 0.075, 0.04, -0.02);
    f.add(new THREE.Mesh(kit.fore[i]!, kit.membrane));
    body.add(f);
    fore.push(f);

    const h = new THREE.Group();
    h.position.set((i === 0 ? -1 : 1) * 0.06, -0.01, 0.06);
    h.add(new THREE.Mesh(kit.hind[i]!, kit.membrane));
    body.add(h);
    hind.push(h);
  }

  // A moth's shadow crossing the carpet is how you notice one, so everything
  // on it casts.
  castShadows(object);

  // Per-instance noise streams. Two moths in one room must not wander in step.
  const seed = (Math.random() * 0xffff) | 0;
  const drift = Math.random() * 90;
  let beat = Math.random() * TAU;
  let yaw = 0;
  let yawInit = false;
  let dead = 0;
  let lastY = HOVER_Y;

  return {
    object,
    update(state, dt, time, _camera) {
      if (!yawInit) {
        yaw = state.yaw;
        yawInit = true;
      }
      yaw = dampAngle(yaw, state.yaw, 2.4, dt);
      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 2.6, dt);
      const alive = 1 - dead;

      const t = time + drift;
      // Three noise streams, all slow enough to hold: where it wants to be,
      // how far off centre it is drifting, and the stall that drops it.
      const climb = noise1(t * 1.45, seed) - 0.5;
      const stall = smoothstep(0.78, 0.97, noise1(t * 0.62, seed + 3));
      const circleR = 0.1 + state.awareness * 0.16;
      const circle = t * (1.1 + state.awareness * 1.5);

      const targetY = HOVER_Y + climb * 0.5 - stall * 0.42;
      const y = lerp(lastY, targetY, 1 - Math.exp(-6 * dt));
      lastY = y;

      object.position.set(
        state.pos.x + Math.cos(circle) * circleR * alive,
        y * alive + 0.075 * dead,
        state.pos.z + Math.sin(circle) * circleR * alive
      );
      // Never quite pointing where it is going.
      object.rotation.y = renderYaw(yaw) + (noise1(t * 1.15, seed + 7) - 0.5) * 0.8 * alive;

      // Fast, and asymmetric: the downstroke snaps and the recovery is slower,
      // which is the difference between a moth and a metronome.
      beat += dt * TAU * (BEAT_HZ + state.awareness * 2.5) * alive;
      const raw = Math.sin(beat);
      const stroke = Math.sign(raw) * Math.pow(Math.abs(raw), 0.68);
      const lag = Math.sin(beat - 0.5);
      const spread = 0.4 + dead * 0.55;

      for (let i = 0; i < 2; i++) {
        const side = i === 0 ? -1 : 1;
        fore[i]!.rotation.z = side * (0.12 + stroke * 0.95 * alive + spread * dead);
        // The twist lags the flap by a quarter cycle. Without it the wing is a
        // hinged board.
        fore[i]!.rotation.x = lag * 0.34 * alive;
        fore[i]!.rotation.y = side * (0.1 - stroke * 0.22) * alive;
        hind[i]!.rotation.z = side * (0.06 + Math.sin(beat - 0.55) * 0.8 * alive + spread * dead);
        hind[i]!.rotation.x = Math.sin(beat - 1.0) * 0.3 * alive;
      }

      // Pitch follows the climb, roll follows nothing in particular. Dead, it
      // is on its back on the carpet with its legs up, which is how they end.
      body.rotation.x = (climb * 0.55 + stall * 0.6) * alive - dead * 0.15;
      body.rotation.z = (noise1(t * 0.9, seed + 13) - 0.5) * 0.7 * alive + dead * Math.PI;
      body.rotation.y = (noise1(t * 0.7, seed + 21) - 0.5) * 0.35 * alive;
      body.position.y = Math.sin(beat) * 0.028 * alive;
    },
    dispose() {
      object.clear();
      mothKit.release();
    },
  };
}
